import _classPrivateFieldLooseBase from "@babel/runtime/helpers/esm/classPrivateFieldLooseBase";
import _classPrivateFieldLooseKey from "@babel/runtime/helpers/esm/classPrivateFieldLooseKey";
import { WINDOW_BROKER_LOGGER_NAME } from '../config';
import { Logger } from 'simple-logging-lib';
import { ObservableMap, Subject } from '../models/observable';
import { generateClientId, getUUID } from '../utils';
import { WindowBrokerConnector } from './window-broker-proxy';
import { filter } from '../models/observable/operators';
import { CrossTabLink } from './cross-tab-link/cross-tab-link';
var _brokerId = /*#__PURE__*/_classPrivateFieldLooseKey("brokerId");
var _allMessages = /*#__PURE__*/_classPrivateFieldLooseKey("allMessages");
var _clientSubscriberMap = /*#__PURE__*/_classPrivateFieldLooseKey("clientSubscriberMap");
var _topicSubscriptionState = /*#__PURE__*/_classPrivateFieldLooseKey("topicSubscriptionState");
var _clientMetadataState = /*#__PURE__*/_classPrivateFieldLooseKey("clientMetadataState");
var _crossTabLink = /*#__PURE__*/_classPrivateFieldLooseKey("crossTabLink");
var _instance = /*#__PURE__*/_classPrivateFieldLooseKey("instance");
var _connector = /*#__PURE__*/_classPrivateFieldLooseKey("connector");
export class WindowBroker {
  constructor() {
    this.logger = Logger.getLogger(WINDOW_BROKER_LOGGER_NAME);
    Object.defineProperty(this, _brokerId, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _allMessages, {
      writable: true,
      value: new Subject()
    });
    Object.defineProperty(this, _clientSubscriberMap, {
      writable: true,
      value: new Map()
    });
    Object.defineProperty(this, _topicSubscriptionState, {
      writable: true,
      value: new ObservableMap()
    });
    Object.defineProperty(this, _clientMetadataState, {
      writable: true,
      value: new ObservableMap()
    });
    Object.defineProperty(this, _crossTabLink, {
      writable: true,
      value: void 0
    });
    this.handshake = ({
      clientMetadata
    }, link) => {
      const clientId = generateClientId();
      _classPrivateFieldLooseBase(this, _clientSubscriberMap)[_clientSubscriberMap].set(clientId, link);
      _classPrivateFieldLooseBase(this, _clientMetadataState)[_clientMetadataState].set(clientId, clientMetadata);
      _classPrivateFieldLooseBase(this, _allMessages)[_allMessages].asObservable().pipe(filter(message => {
        var _classPrivateFieldLoo;
        const topicSubscribers = (_classPrivateFieldLoo = _classPrivateFieldLooseBase(this, _topicSubscriptionState)[_topicSubscriptionState].get(message.topic)) !== null && _classPrivateFieldLoo !== void 0 ? _classPrivateFieldLoo : new Set();
        return topicSubscribers.has(clientId);
      })).subscribe(link);
      return Promise.resolve({
        clientId,
        brokerId: _classPrivateFieldLooseBase(this, _brokerId)[_brokerId]
      });
    };
    this.topicSubscribe = ({
      clientId,
      topic
    }) => {
      var _classPrivateFieldLoo2;
      const topicSubscribers = (_classPrivateFieldLoo2 = _classPrivateFieldLooseBase(this, _topicSubscriptionState)[_topicSubscriptionState].get(topic)) !== null && _classPrivateFieldLoo2 !== void 0 ? _classPrivateFieldLoo2 : new Set();
      topicSubscribers.add(clientId);
      _classPrivateFieldLooseBase(this, _topicSubscriptionState)[_topicSubscriptionState].set(topic, topicSubscribers);
      return Promise.resolve();
    };
    this.topicUnsubscribe = ({
      clientId,
      topic
    }) => {
      var _classPrivateFieldLoo3;
      const topicSubscribers = (_classPrivateFieldLoo3 = _classPrivateFieldLooseBase(this, _topicSubscriptionState)[_topicSubscriptionState].get(topic)) !== null && _classPrivateFieldLoo3 !== void 0 ? _classPrivateFieldLoo3 : new Set();
      topicSubscribers.delete(clientId);
      _classPrivateFieldLooseBase(this, _topicSubscriptionState)[_topicSubscriptionState].set(topic, topicSubscribers);
      return Promise.resolve();
    };
    this.getTopicSubscriptions = ({
      clientId,
      portalId
    }) => {
      const entries = Array.from(_classPrivateFieldLooseBase(this, _topicSubscriptionState)[_topicSubscriptionState].entries());
      const topicNames = entries.filter(([__topic, subscribers]) => subscribers.has(clientId)).map(([topic]) => topic);
      return Promise.resolve({
        clientId,
        portalId,
        topicNames
      });
    };
    this.close = clientId => {
      _classPrivateFieldLooseBase(this, _clientSubscriberMap)[_clientSubscriberMap].delete(clientId);
      _classPrivateFieldLooseBase(this, _clientMetadataState)[_clientMetadataState].delete(clientId);
      _classPrivateFieldLooseBase(this, _topicSubscriptionState)[_topicSubscriptionState].forEach(subscribers => {
        subscribers.delete(clientId);
      });
      return Promise.resolve();
    };
    this.sendMessage = message => {
      const {
        topic
      } = message;
      this.logger.debug(`Broker sending the message '${message.type}' on topic '${topic}'`, message);
      _classPrivateFieldLooseBase(this, _allMessages)[_allMessages].next(message);
      return Promise.resolve();
    };
    this.reset = () => {
      _classPrivateFieldLooseBase(this, _clientSubscriberMap)[_clientSubscriberMap].clear();
      _classPrivateFieldLooseBase(this, _clientMetadataState)[_clientMetadataState].clear();
      _classPrivateFieldLooseBase(this, _topicSubscriptionState)[_topicSubscriptionState].clear();
      _classPrivateFieldLooseBase(WindowBroker, _connector)[_connector].close();
      // @ts-expect-error - Resetting the singleton instance for testing purposes
      _classPrivateFieldLooseBase(WindowBroker, _instance)[_instance] = undefined;
      return Promise.resolve();
    };
    this.getBrokerId = () => _classPrivateFieldLooseBase(this, _brokerId)[_brokerId];
    this.logger.debug('WindowBroker instance created');
    _classPrivateFieldLooseBase(this, _brokerId)[_brokerId] = getUUID();
    _classPrivateFieldLooseBase(WindowBroker, _connector)[_connector] = WindowBrokerConnector.getInstance(this);
    _classPrivateFieldLooseBase(WindowBroker, _connector)[_connector].initialize();
    _classPrivateFieldLooseBase(this, _crossTabLink)[_crossTabLink] = CrossTabLink.getInstance();
    _classPrivateFieldLooseBase(this, _crossTabLink)[_crossTabLink].connect({
      messageLink: _classPrivateFieldLooseBase(this, _allMessages)[_allMessages],
      brokerId: _classPrivateFieldLooseBase(this, _brokerId)[_brokerId]
    });
  }
  static getInstance() {
    if (!_classPrivateFieldLooseBase(WindowBroker, _instance)[_instance]) {
      _classPrivateFieldLooseBase(WindowBroker, _instance)[_instance] = new WindowBroker();
    }
    return _classPrivateFieldLooseBase(WindowBroker, _instance)[_instance];
  }
}
Object.defineProperty(WindowBroker, _instance, {
  writable: true,
  value: void 0
});
Object.defineProperty(WindowBroker, _connector, {
  writable: true,
  value: void 0
});